import type { NlsKey } from '../nls/strings_nls.js'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase.js'
import { callable } from './callable.js'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces.js'
import {
  demoListAssessmentsForSubject,
  demoListDueAssessmentsForWorkspace,
  demoGetAssessment,
  demoGetMark,
} from './eduDemo/eduDemoDirectReads.js'
import { mapEduGradingError } from '../components/Education/classesBuilderHelpers.js'

export { mapEduGradingError }

export function generateUniqueGradingId(prefix: 'term' | 'cat' | 'area', existing: Array<{ id: string }>): string {
  const set = new Set(existing.map((e) => e.id))
  const timeBase = Date.now().toString(36)
  for (let i = 0; i < 100; i++) {
    const rand = Math.random().toString(36).substring(2, 7)
    const candidate = `${prefix}-${timeBase}-${rand}`.slice(0, 40)
    if (!set.has(candidate)) return candidate
  }
  let counter = 1
  while (set.has(`${prefix}-${timeBase}-${counter}`)) {
    counter++
  }
  return `${prefix}-${timeBase}-${counter}`.slice(0, 40)
}

// ---------------------------------------------------------------------------
// Client Types mirroring Cloud Functions Shapes
// ---------------------------------------------------------------------------

export type EduGradingPreset = 'LETTER_AF' | 'CBSE_A1E' | 'PERCENT' | 'PASS_FAIL'

export interface EduGradeBand {
  label: string
  minPercent: number
}

export const PRESET_DEFAULT_BANDS: Record<EduGradingPreset, EduGradeBand[]> = {
  LETTER_AF: [
    { label: 'A', minPercent: 90 },
    { label: 'B', minPercent: 80 },
    { label: 'C', minPercent: 70 },
    { label: 'D', minPercent: 60 },
    { label: 'E', minPercent: 50 },
    { label: 'F', minPercent: 0 },
  ],
  CBSE_A1E: [
    { label: 'A1', minPercent: 91 },
    { label: 'A2', minPercent: 81 },
    { label: 'B1', minPercent: 71 },
    { label: 'B2', minPercent: 61 },
    { label: 'C1', minPercent: 51 },
    { label: 'C2', minPercent: 41 },
    { label: 'D', minPercent: 33 },
    { label: 'E', minPercent: 0 },
  ],
  PERCENT: [],
  PASS_FAIL: [
    { label: 'Pass', minPercent: 50 },
    { label: 'Fail', minPercent: 0 },
  ],
}

export const LETTER_AF: readonly EduGradeBand[] = PRESET_DEFAULT_BANDS.LETTER_AF
export const CBSE_A1E: readonly EduGradeBand[] = PRESET_DEFAULT_BANDS.CBSE_A1E
export const PASS_FAIL = (
  passPercent: number = 50,
  t?: (key: NlsKey, values?: Record<string, string | number>) => string,
): EduGradeBand[] => [
  { label: t ? t('NLSEducationGradingBandPass') : 'Pass', minPercent: passPercent },
  { label: t ? t('NLSEducationGradingBandFail') : 'Fail', minPercent: 0 },
]

export const DEFAULT_CATEGORIES: readonly EduGradingCategory[] = [
  { id: 'exam', name: 'Exam', weight: 40 },
  { id: 'test', name: 'Test', weight: 30 },
  { id: 'homework', name: 'Homework', weight: 15 },
  { id: 'quiz', name: 'Quiz', weight: 15 },
]

export const DEFAULT_INITIAL_CATEGORIES: EduGradingCategory[] = [
  { id: 'exam', name: 'Exam', weight: 40 },
  { id: 'test', name: 'Test', weight: 30 },
  { id: 'homework', name: 'Homework', weight: 15 },
  { id: 'quiz', name: 'Quiz', weight: 15 },
]

export function getDefaultCategories(
  t?: (key: NlsKey, values?: Record<string, string | number>) => string,
): EduGradingCategory[] {
  return [
    { id: 'exam', name: t ? t('NLSEducationGradingCategoryExam') : 'Exam', weight: 40 },
    { id: 'test', name: t ? t('NLSEducationGradingCategoryTest') : 'Test', weight: 30 },
    { id: 'homework', name: t ? t('NLSEducationGradingCategoryHomework') : 'Homework', weight: 15 },
    { id: 'quiz', name: t ? t('NLSEducationGradingCategoryQuiz') : 'Quiz', weight: 15 },
  ]
}

export interface EduGradingCategory {
  id: string
  name: string
  weight: number
}

export interface EduGradingTerm {
  id: string
  name: string
  startDate: string
  endDate: string
  weight?: number
}

export interface EduCoScholasticArea {
  id: string
  name: string
}

export interface EduCoScholasticScaleEntry {
  label: string
  description?: string
}

export interface EduCoScholasticConfig {
  areas: EduCoScholasticArea[]
  scale: EduCoScholasticScaleEntry[]
}

export const DEFAULT_CO_SCHOLASTIC_SCALE: readonly EduCoScholasticScaleEntry[] = [
  { label: 'A' },
  { label: 'B' },
  { label: 'C' },
]

export interface EduGradingScheme {
  workspaceId: string
  rootId: string
  module: 'teaching' | 'training'
  preset: EduGradingPreset
  terms: EduGradingTerm[]
  categories: EduGradingCategory[]
  bands: EduGradeBand[]
  passPercent: number
  version: number
  coScholastic?: EduCoScholasticConfig
  updatedAt?: unknown
  updatedBy: string
}

export interface SetEduGradingSchemeInput {
  workspaceId: string
  rootId: string
  preset: EduGradingPreset
  terms: EduGradingTerm[]
  categories: EduGradingCategory[]
  bands?: EduGradeBand[]
  passPercent: number
  expectedVersion: number
  coScholastic?: EduCoScholasticConfig
}

export interface SetEduGradingSchemeResult {
  success: boolean
  version: number
}

export interface GetEduGradingSchemeInput {
  workspaceId: string
  rootId: string
}

export interface GetEduGradingSchemeResult {
  scheme: EduGradingScheme | null
}

export interface EduAssessment {
  id?: string
  workspaceId: string
  rootId: string
  classId: string | null
  nodeId: string
  module: string
  title: string
  termId: string
  categoryId: string
  termFromDate: boolean
  maxMarks: number
  date: string
  mode: 'OFFLINE' | 'ONLINE'
  source: 'ASSESSMENT' | 'HOMEWORK' | 'QUIZ'
  checklistId: string | null
  status: 'ACTIVE' | 'ARCHIVED'
  readAccess: string[]
  teacherUids: string[]
  createdBy: string
  createdAt?: unknown
  updatedBy: string
  updatedAt?: unknown
}

export interface CreateEduAssessmentInput {
  workspaceId: string
  nodeId: string
  title: string
  termId?: string
  categoryId: string
  maxMarks: number
  date: string
  mode?: 'OFFLINE' | 'ONLINE'
}

export interface CreateEduAssessmentResult {
  success: boolean
  assessmentId: string
}

export interface UpdateEduAssessmentInput {
  workspaceId: string
  assessmentId: string
  title?: string
  termId?: string
  categoryId?: string
  maxMarks?: number
  date?: string
  mode?: 'OFFLINE' | 'ONLINE'
  nodeId?: string
}

export interface UpdateEduAssessmentResult {
  success: boolean
  assessmentId: string
}

export interface ArchiveEduAssessmentInput {
  workspaceId: string
  assessmentId: string
}

export interface ArchiveEduAssessmentResult {
  success: boolean
  assessmentId: string
}

export interface SetEduHomeworkGradingInput {
  workspaceId: string
  checklistId: string
  counts: boolean
  termId?: string
  categoryId?: string
}

export interface SetEduHomeworkGradingResult {
  success: boolean
  assessmentId: string
}

export type EduMarkState = 'MARK' | 'ABSENT' | 'EXCUSED'

export interface EduMarkEntryInput {
  learnerId: string
  state?: EduMarkState | null
  score?: number | null
  remarks?: string | null
}

export interface SetEduMarksInput {
  workspaceId: string
  assessmentId: string
  entries: EduMarkEntryInput[]
}

export interface SetEduMarksResult {
  changed: number
  unchanged: number
}

export interface ListEduMarkHistoryInput {
  workspaceId: string
  markId: string
}

export interface EduMarkHistoryItem {
  id: string
  kind: 'MARK' | 'LOCK'
  action?: string
  from?: { state?: string | null; score?: number | null; remarks?: string | null } | null
  to?: { state?: string | null; score?: number | null; remarks?: string | null } | null
  by?: string
  at?: unknown
  note?: string
}

export type ListEduMarkHistoryResult = EduMarkHistoryItem[] | { history: EduMarkHistoryItem[] }

export interface LockEduMarksInput {
  workspaceId: string
  subjectNodeId: string
  termId: string
  note?: string
}

export interface LockEduMarksResult {
  success: boolean
  status: 'LOCKED'
}

export interface UnlockEduMarksInput {
  workspaceId: string
  subjectNodeId: string
  termId: string
  note?: string
}

export interface UnlockEduMarksResult {
  success: boolean
  status: 'UNLOCKED'
}

export interface RecomputeEduMarksInput {
  workspaceId: string
  subjectNodeId: string
  termId: string
}

export interface RecomputeEduMarksResult {
  success: boolean
  recomputed: number
}

export interface GetEduMarkLockStatusInput {
  workspaceId: string
  subjectNodeId: string
  termId: string
}

export interface MissingMarkAssessment {
  assessmentId: string
  title: string
  count: number
  learnerIds: string[]
}

export interface GetEduMarkLockStatusResult {
  status: 'LOCKED' | 'UNLOCKED' | 'RECOMPUTE_PENDING' | 'RECOMPUTE_INCOMPLETE' | 'NONE'
  lockedAt?: unknown
  lockedBy?: string
  unlockedAt?: unknown
  unlockedBy?: string
  note?: string
  missing?: MissingMarkAssessment[]
  missingMarks?: MissingMarkAssessment[]
  pending?: {
    count: number
    learnerIds: string[]
  }
  events?: Array<Record<string, unknown>>
}

export interface GradebookMarkCell {
  state?: string | null
  score?: number | null
  percent?: number | null
  changedAfterLock?: boolean
  notOnRoll?: boolean
}

export interface GradebookTermSummary {
  categories: Record<string, number | null>
  percent: number | null
  grade: string | null
  missing: number
  changedAfterLock: number
}

export interface GradebookLearnerRow {
  learnerId: string
  displayName: string
  rollNo: string | null
  marks: Record<string, GradebookMarkCell | null>
  terms: Record<string, GradebookTermSummary>
  year: {
    percent: number | null
    grade: string | null
    incompleteTermIds: string[]
  }
}

export interface GetEduGradebookInput {
  workspaceId: string
  subjectNodeId: string
}

export interface GetEduGradebookResult {
  terms: Array<{
    id: string
    name: string
    weight: number
    status: 'LOCKED' | 'UNLOCKED'
  }>
  categories: EduGradingCategory[]
  bands: EduGradeBand[]
  assessments: Array<{
    id: string
    title: string
    termId: string
    categoryId: string
    maxMarks: number
    date: string
    source: string
  }>
  learners: GradebookLearnerRow[]
}

export interface ClassGradebookSubjectSummary {
  terms: Record<
    string,
    {
      percent: number | null
      grade: string | null
      missing: number
      changedAfterLock: number
    }
  >
  year: {
    percent: number | null
    grade: string | null
    incompleteTermIds: string[]
  }
}

export interface ClassGradebookLearnerRow {
  learnerId: string
  displayName: string
  rollNo: string | null
  subjects: Record<string, ClassGradebookSubjectSummary | null>
}

export interface GetEduClassGradebookInput {
  workspaceId: string
  classId: string
}

export interface GetEduClassGradebookResult {
  terms: Array<{
    id: string
    name: string
    weight: number
  }>
  subjects: Array<{
    id: string
    name: string
  }>
  learners: ClassGradebookLearnerRow[]
  locks?: Record<string, Record<string, 'LOCKED' | 'UNLOCKED'>>
}

// ---------------------------------------------------------------------------
// Cloud Functions Callables
// ---------------------------------------------------------------------------

export async function setEduGradingScheme(
  input: SetEduGradingSchemeInput,
): Promise<SetEduGradingSchemeResult> {
  return callable<SetEduGradingSchemeInput, SetEduGradingSchemeResult>('setEduGradingScheme', input)
}

export async function getEduGradingScheme(
  input: GetEduGradingSchemeInput,
): Promise<GetEduGradingSchemeResult> {
  return callable<GetEduGradingSchemeInput, GetEduGradingSchemeResult>('getEduGradingScheme', input)
}

export async function createEduAssessment(
  input: CreateEduAssessmentInput,
): Promise<CreateEduAssessmentResult> {
  return callable<CreateEduAssessmentInput, CreateEduAssessmentResult>('createEduAssessment', input)
}

export async function updateEduAssessment(
  input: UpdateEduAssessmentInput,
): Promise<UpdateEduAssessmentResult> {
  return callable<UpdateEduAssessmentInput, UpdateEduAssessmentResult>('updateEduAssessment', input)
}

export async function archiveEduAssessment(
  input: ArchiveEduAssessmentInput,
): Promise<ArchiveEduAssessmentResult> {
  return callable<ArchiveEduAssessmentInput, ArchiveEduAssessmentResult>('archiveEduAssessment', input)
}

export async function setEduHomeworkGrading(
  input: SetEduHomeworkGradingInput,
): Promise<SetEduHomeworkGradingResult> {
  return callable<SetEduHomeworkGradingInput, SetEduHomeworkGradingResult>('setEduHomeworkGrading', input)
}

export async function setEduMarks(input: SetEduMarksInput): Promise<SetEduMarksResult> {
  return callable<SetEduMarksInput, SetEduMarksResult>('setEduMarks', input)
}

export async function listEduMarkHistory(
  input: ListEduMarkHistoryInput,
): Promise<ListEduMarkHistoryResult> {
  return callable<ListEduMarkHistoryInput, ListEduMarkHistoryResult>('listEduMarkHistory', input)
}

export async function lockEduMarks(input: LockEduMarksInput): Promise<LockEduMarksResult> {
  return callable<LockEduMarksInput, LockEduMarksResult>('lockEduMarks', input)
}

export async function unlockEduMarks(input: UnlockEduMarksInput): Promise<UnlockEduMarksResult> {
  return callable<UnlockEduMarksInput, UnlockEduMarksResult>('unlockEduMarks', input)
}

export async function recomputeEduMarks(
  input: RecomputeEduMarksInput,
): Promise<RecomputeEduMarksResult> {
  return callable<RecomputeEduMarksInput, RecomputeEduMarksResult>('recomputeEduMarks', input)
}

export async function getEduMarkLockStatus(
  input: GetEduMarkLockStatusInput,
): Promise<GetEduMarkLockStatusResult> {
  return callable<GetEduMarkLockStatusInput, GetEduMarkLockStatusResult>('getEduMarkLockStatus', input)
}

export async function getEduGradebook(
  input: GetEduGradebookInput,
): Promise<GetEduGradebookResult> {
  return callable<GetEduGradebookInput, GetEduGradebookResult>('getEduGradebook', input)
}

export async function getEduClassGradebook(
  input: GetEduClassGradebookInput,
): Promise<GetEduClassGradebookResult> {
  return callable<GetEduClassGradebookInput, GetEduClassGradebookResult>('getEduClassGradebook', input)
}

// ---------------------------------------------------------------------------
// Firestore Queries
// ---------------------------------------------------------------------------

export async function listAssessmentsForSubject(
  workspaceId: string,
  nodeId: string,
  options?: { includeArchived?: boolean; restrictedUid?: string },
): Promise<EduAssessment[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListAssessmentsForSubject(workspaceId, nodeId, options)
  }

  const restrictedUid = options?.restrictedUid
  const includeArchived = options?.includeArchived ?? false

  const buildQuery = (status: 'ACTIVE' | 'ARCHIVED') => {
    const constraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('nodeId', '==', nodeId),
      where('status', '==', status),
    ]
    if (restrictedUid) {
      constraints.push(where('readAccess', 'array-contains', restrictedUid))
    }
    constraints.push(orderBy('date', 'desc'))
    return query(collection(firestoreDb, 'edu_assessments'), ...constraints)
  }

  if (includeArchived) {
    const [activeSnap, archivedSnap] = await Promise.all([
      getDocs(buildQuery('ACTIVE')),
      getDocs(buildQuery('ARCHIVED')),
    ])
    const all: EduAssessment[] = [
      ...activeSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EduAssessment, 'id'>) })),
      ...archivedSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EduAssessment, 'id'>) })),
    ]
    all.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    return all
  }

  const snap = await getDocs(buildQuery('ACTIVE'))
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EduAssessment, 'id'>) }))
}

export async function listDueAssessmentsForWorkspace(
  workspaceId: string,
  fromDate: string,
  toDate: string,
  options?: { restrictedUid?: string },
): Promise<EduAssessment[]> {
  if (!workspaceId) return []
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListDueAssessmentsForWorkspace(workspaceId, fromDate, toDate, options)
  }

  const restrictedUid = options?.restrictedUid
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'ACTIVE'),
  ]
  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }
  constraints.push(
    where('date', '>=', fromDate),
    where('date', '<=', toDate),
    orderBy('date', 'asc'),
  )
  const q = query(collection(firestoreDb, 'edu_assessments'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EduAssessment, 'id'>) }))
}

export async function getAssessment(
  workspaceId: string,
  id: string,
): Promise<EduAssessment | null> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoGetAssessment(workspaceId, id)
  }

  const ref = doc(firestoreDb, 'edu_assessments', id)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const data = snap.data() as EduAssessment
  if (data.workspaceId !== workspaceId) return null
  return { id: snap.id, ...data }
}

export interface EduMark {
  state?: EduMarkState | null
  score?: number | null
  remarks?: string | null
}

export async function getMark(
  workspaceId: string,
  assessmentId: string,
  learnerId: string,
): Promise<EduMark | null> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoGetMark(workspaceId, assessmentId, learnerId)
  }

  const markId = `${assessmentId}_${learnerId}`
  const ref = doc(firestoreDb, 'edu_marks', markId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const data = snap.data() as { workspaceId?: string; state?: EduMarkState | null; score?: number | null; remarks?: string | null }
  if (data.workspaceId !== workspaceId) return null
  return {
    state: data.state ?? null,
    score: data.score ?? null,
    remarks: data.remarks ?? null,
  }
}

export function parseMarkCell(
  text: string,
  maxMarks: number,
): { state: EduMarkState | null; score?: number } | { error: string } {
  const trimmed = text.trim()
  if (!trimmed) {
    return { state: null }
  }
  const upper = trimmed.toUpperCase()
  if (upper === 'AB' || upper === 'A') {
    return { state: 'ABSENT' }
  }
  if (upper === 'EX' || upper === 'E') {
    return { state: 'EXCUSED' }
  }
  if (/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    const num = Number(trimmed)
    if (!Number.isNaN(num) && num >= 0 && num <= maxMarks) {
      return { state: 'MARK', score: num }
    }
  }
  return { error: 'INVALID_SCORE' }
}

export function parsePasteBlock(text: string): string[][] {
  let cleanText = text
  if (cleanText.endsWith('\r\n')) {
    cleanText = cleanText.slice(0, -2)
  } else if (cleanText.endsWith('\n') || cleanText.endsWith('\r')) {
    cleanText = cleanText.slice(0, -1)
  }
  if (!cleanText) return []
  const lines = cleanText.split(/\r?\n/)
  return lines.map((line) => line.split('\t'))
}

